// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/pages/application/StoreSetupPage.tsx
//
// Phase 1 Wave 12 Task 99 — Vendor application step 2.
// Phase 7 Wave 18 Task 78 batch C6 (Amendment #1) — i18n rewrite.

import { useNavigate } from 'react-router-dom';
import {
  Button,
  Form,
  FormField,
  FormLabel,
  Input,
  VendorApplicationStepperLayout,
  cn,
} from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import { useApplicationStore } from './ApplicationStore.js';

const EQUIPMENT_KEYS = [
  { value: 'DTG Printer', key: 'application.storeSetup.equipmentDTG' },
  { value: 'Sublimation Press', key: 'application.storeSetup.equipmentSublimation' },
  { value: 'Heat Press', key: 'application.storeSetup.equipmentHeatPress' },
  { value: 'Embroidery Machine', key: 'application.storeSetup.equipmentEmbroidery' },
  { value: 'Vinyl Cutter', key: 'application.storeSetup.equipmentVinyl' },
  { value: 'Laser Engraver', key: 'application.storeSetup.equipmentLaser' },
] as const;

export function StoreSetupPage(): JSX.Element {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const data = useApplicationStore((s) => s.data.storeSetup);
  const setStoreSetup = useApplicationStore((s) => s.setStoreSetup);
  const toggleEquipment = useApplicationStore((s) => s.toggleEquipment);

  const STORE_TYPES = [
    { value: 't-shirts', label: t('application.storeSetup.typeTshirts') },
    { value: 'mugs', label: t('application.storeSetup.typeMugs') },
    { value: 'mixed', label: t('application.storeSetup.typeMixed') },
  ] as const;

  const canContinue =
    data.storeName.trim().length > 0 &&
    data.storeType !== '' &&
    data.capacityPerDay.trim().length > 0;

  return (
    <VendorApplicationStepperLayout currentStep={2}>
      <h2 className="text-xl font-semibold text-pw-text">{t('application.storeSetup.heading')}</h2>
      <p className="mt-1 text-sm text-pw-text-muted">
        {t('application.storeSetup.description')}
      </p>

      <Form className="mt-6 space-y-4" onSubmit={(e) => e.preventDefault()}>
        <FormField>
          <FormLabel htmlFor="storeName">{t('application.storeSetup.storeName')}</FormLabel>
          <Input
            id="storeName"
            value={data.storeName}
            onChange={(e) => setStoreSetup({ storeName: e.target.value })}
            placeholder={t('application.storeSetup.storeNamePlaceholder')}
          />
        </FormField>

        <FormField>
          <FormLabel>{t('application.storeSetup.storeType')}</FormLabel>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {STORE_TYPES.map((opt) => {
              const checked = data.storeType === opt.value;
              return (
                <label
                  key={opt.value}
                  className={cn(
                    'flex cursor-pointer items-center justify-center rounded-lg border-2 p-3 text-sm font-medium transition-colors',
                    checked
                      ? 'border-pw-primary bg-pw-primary/5 text-pw-text'
                      : 'border-pw-border bg-pw-bg text-pw-text-muted hover:border-pw-primary/40',
                  )}
                >
                  <input
                    type="radio"
                    name="storeType"
                    value={opt.value}
                    checked={checked}
                    onChange={() => setStoreSetup({ storeType: opt.value })}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
        </FormField>

        <FormField>
          <FormLabel htmlFor="capacityPerDay">{t('application.storeSetup.capacity')}</FormLabel>
          <Input
            id="capacityPerDay"
            type="number"
            min="0"
            value={data.capacityPerDay}
            onChange={(e) => setStoreSetup({ capacityPerDay: e.target.value })}
            placeholder={t('application.storeSetup.capacityPlaceholder')}
          />
        </FormField>

        <FormField>
          <FormLabel>{t('application.storeSetup.equipment')}</FormLabel>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {EQUIPMENT_KEYS.map((eq) => {
              const checked = data.equipment.includes(eq.value);
              return (
                <label
                  key={eq.value}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition-colors',
                    checked
                      ? 'border-pw-primary bg-pw-primary/5'
                      : 'border-pw-border bg-pw-bg hover:border-pw-primary/40',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleEquipment(eq.value)}
                    className="h-4 w-4 accent-pw-primary"
                  />
                  <span className="text-pw-text">{t(eq.key)}</span>
                </label>
              );
            })}
          </div>
        </FormField>

        <div className="flex items-center justify-between pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/application/business-info')}
          >
            {t('application.storeSetup.back')}
          </Button>
          <Button
            type="button"
            disabled={!canContinue}
            onClick={() => navigate('/application/documents')}
          >
            {t('application.storeSetup.continue')}
          </Button>
        </div>
      </Form>
    </VendorApplicationStepperLayout>
  );
}

export default StoreSetupPage;
